import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import config from "../../config";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { setUserData } from "../../store/userSlice";

function Photo() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const { telegramId, userId } = useSelector((state) => state.auth);
  const userData = useSelector((state) => state.user.userData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photos, setPhotos] = useState({});
  const [originalPhotos, setOriginalPhotos] = useState([]); // Сохраняем оригинальные фото
  const fileInputs = useRef({});

  // Drag and drop states
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchStartX, setTouchStartX] = useState(null);
  const [ghostPhoto, setGhostPhoto] = useState(null);
  const [ghostStyle, setGhostStyle] = useState({});

  // На страницу можно попасть из разных мест (например, с экрана знакомств) —
  // redux может не содержать свежих данных, поэтому подтягиваем их сами
  React.useEffect(() => {
    if (!userId) return;

    axiosPrivate
      .get(`/users/${userId}`)
      .then((response) => dispatch(setUserData(response.data)))
      .catch((error) => console.error("Error fetching user data:", error));
  }, [userId]);

  // Initialize photos from userData
  React.useEffect(() => {
    console.log("UserData photos:", userData?.photos);
    if (userData?.photos && Array.isArray(userData.photos)) {
      // Сохраняем оригинальные фотографии для отслеживания изменений
      setOriginalPhotos(userData.photos);

      // Конвертируем в объект для управления
      const photosObj = userData.photos.reduce((acc, photoUrl, index) => {
        if (photoUrl) {
          acc[index + 1] = {
            url: photoUrl,
            originalIndex: index,
            isNew: false,
          };
        }
        return acc;
      }, {});
      console.log("Converted photos object:", photosObj);
      setPhotos(photosObj);
    }
  }, [userData]);


  const handlePhotoSelect = (photoId) => {
    fileInputs.current[photoId]?.click();
  };

  const handleFileChange = (e, photoId) => {
    const file = e.target.files[0];
    if (file) {
      setPhotos((prev) => ({
        ...prev,
        [photoId]: {
          file: file,
          preview: URL.createObjectURL(file),
          isNew: true,
        },
      }));
    }
  };

  const handleDeletePhoto = (photoId) => {
    setPhotos((prev) => {
      const newPhotos = { ...prev };
      delete newPhotos[photoId];
      return newPhotos;
    });
  };

  // Drag and drop functions
  const handleDragStart = (photoId) => {
    setDraggedIndex(photoId);
    document.body.classList.add("dragging");
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    document.body.classList.remove("dragging");
  };

  const handleDragOver = (e, photoId) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== photoId) {
      e.currentTarget.classList.add("drag-over");
    }
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("drag-over");
  };

  const handleDrop = (e, targetPhotoId) => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");

    if (draggedIndex !== null && draggedIndex !== targetPhotoId) {
      reorderPhotos(draggedIndex, targetPhotoId);
    }
  };

  // Touch drag support
  const handleTouchStart = (e, photoId) => {
    const photo = photos[photoId];
    if (!photo || (!photo.url && !photo.preview)) return;

    setDraggedIndex(photoId);
    setGhostPhoto(photo.url || photo.preview);
    setTouchStartY(e.touches[0].clientY);
    setTouchStartX(e.touches[0].clientX);

    // Предотвращаем скролл страницы во время перетаскивания
    e.preventDefault();
  };

  const handleTouchMove = (e) => {
    if (draggedIndex === null) return;

    const touch = e.touches[0];
    setGhostStyle({
      top: touch.clientY - 80 + "px",
      left: touch.clientX - 80 + "px",
      position: "fixed",
      width: "160px",
      height: "160px",
      zIndex: 1000,
      pointerEvents: "none",
      opacity: 0.8,
    });

    e.preventDefault();
  };

  const handleTouchEnd = (e) => {
    if (draggedIndex === null) return;

    const touch = e.changedTouches[0];
    const dropTarget = document.elementFromPoint(touch.clientX, touch.clientY);
    const targetPhotoId = Number(
      dropTarget?.closest("[data-photo-id]")?.getAttribute("data-photo-id")
    );

    if (!isNaN(targetPhotoId) && targetPhotoId !== draggedIndex) {
      reorderPhotos(draggedIndex, targetPhotoId);
    }

    setDraggedIndex(null);
    setGhostPhoto(null);
  };

  // Function to reorder photos
  const reorderPhotos = (fromPhotoId, toPhotoId) => {
    console.log(`Reordering photos: from ${fromPhotoId} to ${toPhotoId}`);
    setPhotos((prev) => {
      const newPhotos = { ...prev };

      // Получаем фото, которое перетаскиваем
      const draggedPhoto = newPhotos[fromPhotoId];
      const targetPhoto = newPhotos[toPhotoId];

      if (draggedPhoto && targetPhoto) {
        // Меняем местами
        console.log("Swapping photos:", draggedPhoto, "with", targetPhoto);
        newPhotos[fromPhotoId] = targetPhoto;
        newPhotos[toPhotoId] = draggedPhoto;
      } else if (draggedPhoto && !targetPhoto) {
        // Перемещаем в пустое место
        console.log("Moving photo to empty slot:", draggedPhoto);
        newPhotos[toPhotoId] = draggedPhoto;
        delete newPhotos[fromPhotoId];
      }

      console.log("New photos state:", newPhotos);
      return newPhotos;
    });
  };

  // Функция для извлечения ключа из S3 URL
  const extractKeyFromUrl = (url) => {
    if (!url) return null;
    try {
      // Для presigned URLs и обычных S3 URLs
      if (url.includes("amazonaws.com/")) {
        const parts = url.split("amazonaws.com/");
        if (parts.length > 1) {
          // Убираем query параметры
          return decodeURIComponent(parts[1].split("?")[0]);
        }
      }
      // Fallback - пытаемся извлечь последнюю часть пути
      const pathname = new URL(url).pathname;
      return pathname.startsWith("/") ? pathname.substring(1) : pathname;
    } catch (e) {
      console.warn("Failed to extract key from URL:", url, e);
      return null;
    }
  };

  // Проверяем, есть ли изменения
  const hasChanges = () => {
    const currentPhotoIds = Object.keys(photos).map(Number).sort();
    const hasNewPhotos = Object.values(photos).some((photo) => photo.isNew);
    const hasDeletedPhotos = currentPhotoIds.length !== originalPhotos.length;

    // Проверяем изменение порядка - сравниваем URL фотографий по позициям
    const hasReorderedPhotos = originalPhotos.some((originalUrl, index) => {
      const currentPhotoAtPosition = photos[index + 1];
      return (
        originalUrl &&
        currentPhotoAtPosition &&
        currentPhotoAtPosition.url !== originalUrl &&
        !currentPhotoAtPosition.isNew
      );
    });

    return hasNewPhotos || hasDeletedPhotos || hasReorderedPhotos;
  };

  const handleSave = async () => {
    if (!hasChanges()) {
      navigate(-1);
      return;
    }

    try {
      setIsSubmitting(true);

      // Сначала загружаем все новые фотографии
      const newPhotos = Object.entries(photos).filter(
        ([photoId, photo]) => photo.isNew && photo.file
      );

      // Загружаем новые файлы и получаем их ключи
      const uploadedPhotoKeys = {};
      for (const [photoId, photoData] of newPhotos) {
        try {
          const formData = new FormData();
          formData.append("file", photoData.file);

          console.log("Uploading new photo...");
          const response = await axiosPrivate.post("/users/photo", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          // Сохраняем ключ загруженной фотографии
          uploadedPhotoKeys[photoId] = response.data.data.key;
        } catch (error) {
          console.error("Failed to upload photo:", error);
          throw error;
        }
      }

      // Теперь создаем правильный порядок фотографий
      const orderedPhotoKeys = [];
      for (let i = 1; i <= 4; i++) {
        const photo = photos[i];
        if (photo) {
          if (photo.isNew) {
            // Новая фотография - используем загруженный ключ
            orderedPhotoKeys.push(uploadedPhotoKeys[i]);
          } else {
            // Существующая фотография - извлекаем ключ из URL
            const key = extractKeyFromUrl(photo.url);
            if (key) {
              orderedPhotoKeys.push(key);
            }
          }
        }
      }

      // Отправляем обновленный порядок фотографий на сервер
      if (orderedPhotoKeys.length > 0) {
        try {
          console.log("Sending new photos order to server:", orderedPhotoKeys);
          const orderResponse = await axiosPrivate.put("/users/photos-order", {
            photos: orderedPhotoKeys,
          });
          console.log("Photos order updated successfully:", orderResponse.data);
        } catch (error) {
          // Если эндпоинт не существует, пропускаем ошибку
          console.warn(
            "Failed to update photos order, endpoint may not exist:",
            error
          );
        }
      }

      // Удаляем фотографии, которые больше не нужны
      const currentPhotoKeys = orderedPhotoKeys;
      const photosToDelete = [];

      originalPhotos.forEach((originalUrl, index) => {
        if (originalUrl) {
          const key = extractKeyFromUrl(originalUrl);
          if (key && !currentPhotoKeys.includes(key)) {
            photosToDelete.push(key);
          }
        }
      });

      // Удаляем ненужные фотографии
      for (const photoKey of photosToDelete) {
        try {
          console.log("Deleting photo with key:", photoKey);
          await axiosPrivate.delete(
            `/users/photo/${encodeURIComponent(photoKey)}`
          );
        } catch (error) {
          console.warn("Failed to delete photo:", photoKey, error);
        }
      }

      console.log("Photos updated successfully");

      // Перезагружаем данные пользователя, чтобы обновить порядок фотографий в Redux
      try {
        const updatedUserResponse = await axiosPrivate.get(`/users/${userId}`);
        dispatch(setUserData(updatedUserResponse.data));
      } catch (updateError) {
        console.warn("Failed to update user data in Redux:", updateError);
      }

      navigate(-1);
    } catch (error) {
      console.error("Error updating photos:", error);
      alert(
        "Произошла ошибка при сохранении фотографий. Пожалуйста, попробуйте еще раз."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-[100vw] flex items-center justify-center">
      <div className="flex flex-col items-center w-[343px] h-screen overflow-y-auto overflow-x-hidden">
        <div className="mt-[32px] mb-[30px] w-full flex-row text-[18px] flex justify-between">
          <span
            className="text-white"
            onClick={() => {
              navigate(-1);
            }}
          >
            Отмена
          </span>
          <span
            className={`${
              isSubmitting
                ? "opacity-50"
                : hasChanges()
                ? "text-[#A1F69E]"
                : "text-gray-400"
            }`}
            onClick={isSubmitting ? undefined : handleSave}
          >
            {isSubmitting ? "Сохранение..." : "Сохранить"}
          </span>
        </div>
        <h1 className="font-raleway font-semibold mt-6 text-white text-[20px]">
          Добавьте фото
        </h1>
        <h1 className="font-raleway font-light mt-2 text-white text-[16px]">
          Минимум одно, а лучше – все четыре
        </h1>
        <div
          className={`w-[343] h-[343] mb-[30px] mt-5 grid grid-cols-2 gap-[15px]`}
        >
          {[1, 2, 3, 4].map((photoId) => (
            <React.Fragment key={photoId}>
              <input
                type="file"
                ref={(el) => (fileInputs.current[photoId] = el)}
                onChange={(e) => handleFileChange(e, photoId)}
                accept="image/*"
                className="hidden"
              />
              <div
                id={`photo${photoId}`}
                data-photo-id={photoId}
                draggable={!!photos[photoId]}
                onClick={() => handlePhotoSelect(photoId)}
                onDragStart={() => handleDragStart(photoId)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, photoId)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, photoId)}
                onTouchStart={(e) => handleTouchStart(e, photoId)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className={`w-[164px] h-[164px] border-[1px] rounded-[16px] border-[#233636] bg-[#022424] relative flex items-center justify-center cursor-pointer
                  ${draggedIndex === photoId ? "opacity-50" : ""}
                  transition-all duration-200 ease-in-out
                  hover:border-[#A1F69E]
                  drag-over:border-[#A1F69E]
                  drag-over:scale-105`}
              >
                {photoId === 1 && (
                  <div className="absolute top-0 left-0 z-10">
                    <img alt="Главное фото" src="/icons/main_photo_label.svg" />
                  </div>
                )}
                {photos[photoId]?.preview ? (
                  <>
                    <img
                      src={photos[photoId].preview}
                      alt={`Photo ${photoId}`}
                      className="w-full h-full object-cover rounded-[16px] select-none"
                    />
                    {photoId === 1 && (
                      <div className="absolute bottom-0 left-0 right-0 h-[40px] flex items-center justify-center bg-gradient-to-t from-[#032B2B] to-transparent rounded-b-[16px]">
                        <span className="text-white font-raleway text-[14px]">
                          Главное фото
                        </span>
                      </div>
                    )}
                    <div
                      className="absolute top-[8px] right-[8px] pointer-events-auto z-10 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePhoto(photoId);
                      }}
                    >
                      <img
                        src="/icons/delete_img.svg"
                        className="w-[16px] h-[16px]"
                        alt="Удалить"
                      />
                    </div>
                  </>
                ) : photos[photoId]?.url ? (
                  <>
                    <img
                      src={photos[photoId].url}
                      alt={`Photo ${photoId}`}
                      className="w-full h-full object-cover rounded-[16px] select-none"
                    />
                    {photoId === 1 && (
                      <div className="absolute bottom-0 left-0 right-0 h-[40px] flex items-center justify-center bg-gradient-to-t from-[#032B2B] to-transparent rounded-b-[16px]">
                        <span className="text-white font-raleway text-[14px]">
                          Главное фото
                        </span>
                      </div>
                    )}
                    <div
                      className="absolute top-[8px] right-[8px] pointer-events-auto z-10 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePhoto(photoId);
                      }}
                    >
                      <img
                        src="/icons/delete_img.svg"
                        className="w-[16px] h-[16px]"
                        alt="Удалить"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <img alt="Камера" src="/icons/camera.svg" />
                  </>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
        <h1 className="font-raleway font-light text-white text-[16px]">
          Перетащите, чтобы изменить порядок
        </h1>

        {/* 👻 Призрак фото для touch drag */}
        {ghostPhoto && (
          <img
            src={ghostPhoto}
            style={ghostStyle}
            className="rounded-[16px] shadow-lg transition-none"
            alt="Призрак фото"
          />
        )}
      </div>
    </div>
  );
}

export default Photo;
